import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

const CollaboratorDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newService, setNewService] = useState({ name: '', description: '', duration: '', trainer: '' });
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', start: '', end: '', description: '', trainer: '' });


  useEffect(() => {
    // Load calendar events
    const eventsQuery = query(collection(db, 'events'), orderBy('start', 'asc'));
    const eventsUnsubscribe = onSnapshot(eventsQuery, (snapshot) => {
      const eventsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEvents(eventsData);
    });

    // Load services
    const servicesQuery = query(collection(db, 'services'), orderBy('createdAt', 'desc'));
    const servicesUnsubscribe = onSnapshot(servicesQuery, (snapshot) => {
      const servicesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setServices(servicesData);
    });

    // Load messages
    const messagesQuery = query(collection(db, 'messages'), orderBy('createdAt', 'desc'));
    const messagesUnsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
    });

    return () => {
      eventsUnsubscribe();
      servicesUnsubscribe();
      messagesUnsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDateClick = (arg) => {
    setNewEvent({ ...newEvent, start: arg.dateStr, end: arg.dateStr });
    setShowEventModal(true);
  };

  const handleEventAdd = async () => {
    if (!newEvent.title || !newEvent.start) return;

    try {
      await addDoc(collection(db, 'events'), {
        ...newEvent,
        createdBy: user.email,
        createdAt: new Date().toISOString()
      });
      setShowEventModal(false);
      setNewEvent({ title: '', start: '', end: '', description: '', trainer: '' });
    } catch (error) {
      console.error('Error adding event:', error);
    }
  };

  const handleServiceAdd = async () => {
    if (!newService.name || !newService.description) return;

    try {
      await addDoc(collection(db, 'services'), {
        ...newService,
        createdBy: user.email,
        createdAt: new Date().toISOString()
      });
      setNewService({ name: '', description: '', duration: '', trainer: '' });
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleMessageSend = async () => {
    if (!newMessage.trim()) return;

    try {
      await addDoc(collection(db, 'messages'), {
        text: newMessage,
        sender: user.email,
        createdAt: new Date().toISOString()
      });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleServiceDelete = async (serviceId) => {
    try {
      await deleteDoc(doc(db, 'services', serviceId));
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-light text-gray-900">Collaborator Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome, {user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('calendar')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'calendar'
                  ? 'border-spi-500 text-spi-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Calendar Development
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'services'
                  ? 'border-spi-500 text-spi-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Services Management
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'messages'
                  ? 'border-spi-500 text-spi-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Team Messages
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Program Calendar</h2>
            <p className="text-gray-600 mb-6">Click on a date to add events for the pilot program</p>
            
            <div className="mb-6">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={events}
                dateClick={handleDateClick}
                height="600px"
                eventColor="#1e40af"
              />
            </div>

            {/* Event Modal */}
            {showEventModal && (
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <h3 className="text-lg font-semibold mb-4">Add New Event</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Title</label>
                      <input
                        type="text"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Start Date/Time</label>
                      <input
                        type="datetime-local"
                        value={newEvent.start}
                        onChange={(e) => setNewEvent({...newEvent, start: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">End Date/Time</label>
                      <input
                        type="datetime-local"
                        value={newEvent.end}
                        onChange={(e) => setNewEvent({...newEvent, end: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Trainer/Instructor</label>
                      <select
                        value={newEvent.trainer}
                        onChange={(e) => setNewEvent({...newEvent, trainer: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      >
                        <option value="">Select Trainer</option>
                        <option value="Justin King">Justin King</option>
                        <option value="Michael Susi">Michael Susi</option>
                        <option value="Team Beast Mode">Team Beast Mode</option>
                        <option value="Dr. Monos">Dr. Monos</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        value={newEvent.description}
                        onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        rows="3"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setShowEventModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEventAdd}
                      className="px-4 py-2 bg-spi-600 text-white rounded-md hover:bg-spi-700"
                    >
                      Add Event
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Services Management</h2>
            
            {/* Add Service Form */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Add New Service</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Service Name</label>
                  <input
                    type="text"
                    value={newService.name}
                    onChange={(e) => setNewService({...newService, name: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="e.g., Speed Training"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Trainer</label>
                  <select
                    value={newService.trainer}
                    onChange={(e) => setNewService({...newService, trainer: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  >
                    <option value="">Select Trainer</option>
                    <option value="Justin King">Justin King</option>
                    <option value="Michael Susi">Michael Susi</option>
                    <option value="Team Beast Mode">Team Beast Mode</option>
                    <option value="Dr. Monos">Dr. Monos</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration</label>
                  <input
                    type="text"
                    value={newService.duration}
                    onChange={(e) => setNewService({...newService, duration: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="e.g., 60 minutes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newService.description}
                    onChange={(e) => setNewService({...newService, description: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                    rows="3"
                    placeholder="Service description..."
                  />
                </div>
              </div>
              <button
                onClick={handleServiceAdd}
                className="mt-4 px-4 py-2 bg-spi-600 text-white rounded-md hover:bg-spi-700"
              >
                Add Service
              </button>
            </div>

            {/* Services List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => (
                <div key={service.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-lg">{service.name}</h4>
                      <p className="text-sm text-gray-600">Trainer: {service.trainer}</p>
                      <p className="text-sm text-gray-600">Duration: {service.duration}</p>
                      <p className="mt-2 text-gray-700">{service.description}</p>
                    </div>
                    <button
                      onClick={() => handleServiceDelete(service.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Team Communication</h2>
            
            {/* Message List */}
            <div className="h-96 overflow-y-auto border rounded-lg p-4 mb-4">
              {messages.length === 0 ? (
                <p className="text-gray-500 text-center">No messages yet. Start the conversation!</p>
              ) : (
                <div className="space-y-3">
                  {messages.map((message) => (
                    <div key={message.id} className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-spi-600 text-white rounded-full flex items-center justify-center text-sm">
                          {message.sender.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{message.sender}</p>
                        <p className="text-sm text-gray-600">{message.text}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(message.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Message Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleMessageSend()}
                className="flex-1 border-gray-300 rounded-md shadow-sm"
                placeholder="Type your message..."
              />
              <button
                onClick={handleMessageSend}
                className="px-4 py-2 bg-spi-600 text-white rounded-md hover:bg-spi-700"
              >
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaboratorDashboard;